import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Divider, Form } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';

import iconErrorSvg from 'assets/images/error-red.svg';
import guarantorYellow from 'assets/images/guarantor-yellow.svg';
import InputBorderNone from 'components/Atoms/Inputs/InputBorderNone/InputBorderNone';
import { File, Loan, Task, TaskState } from 'gql/graphql';
import { useCreateResponseTask, useRemoveTaskResponse } from 'services/task/mutations';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import ModalCompleteProfile from './ModalComplete';
import ModalFinishTask from './ModalFinishTask';
import FormEdit from './FormEdit';
import { useGetTaskResponsesByTask } from '../../../../../../services/task/querys';
import TagOwnerState from '../Step2/CardOwnershipEdit/TagOwnerState';
import { useCreateOwnership, useDeleteOwnership } from '../../../../../../services/ownership/mutations';
import { LoanContext } from '../../../Dashboard';
import IssuesTaskResponse from 'scenes/Private/components/IssuesTaskResponse/IssuesTaskResponse';
import ErrorBox from 'components/Atoms/Alerts/ErrorBox';
import alertIcon from '../../../../../../assets/images/alert-triangle.svg';

export interface Ownership {
  _id?: string;
  key: string;
  name: string;
  lastname: string;
  ownership: number;
  dateBirth: string;
  email: string;
  phone: string;
  ssn: string;
  address: string;
  state: string;
  stakes: any;
  files: File[];
  taskResponse: any;
}

interface Props {
  index?: any;
  ownership: Ownership;
  ownerEdits?: any[];
  hiddenSectionViewProfile?: boolean;
  hiddenGuarantorTag?: boolean;
  hiddenTextEdit?: boolean;
  disabledForm?: boolean;
  hiddenSubmitChanges?: boolean;
  showTagState?: boolean;
  hiddenColorsState?: boolean;
  disabledPercentage?: boolean;
  isModalSubmit?: boolean;
  ownerships?: Ownership[];
  closeTask?: Function;
  isActionRemoved?: boolean;
  enabledDataBasic?: boolean;
  otherButton?: any;
  stepVerify?: boolean;
  isOwnerModelEdit?: boolean;
  entity?: any;
  onDeleteOwner?: any;
  isExpand?: any;
  setNewPercentageLiveEdit?: any;
  handlerSwitchExpandCard?: any;
  disabledRemovedAction?: boolean;
}

let isUpdatePercentage = false;

const CardOwnership = ({
  ownership,
  ownerEdits,
  hiddenSectionViewProfile,
  hiddenGuarantorTag,
  hiddenTextEdit,
  disabledForm,
  hiddenSubmitChanges,
  showTagState,
  hiddenColorsState,
  isModalSubmit,
  ownerships,
  closeTask,
  disabledPercentage,
  enabledDataBasic,
  isActionRemoved,
  otherButton,
  stepVerify,
  isOwnerModelEdit,
  entity,
  onDeleteOwner,
  isExpand,
  index,
  setNewPercentageLiveEdit,
  handlerSwitchExpandCard,
  disabledRemovedAction
}: Props) => {
  const [form] = Form.useForm();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loan: Loan | any = useContext(LoanContext);

  const { data: responseCreateOwner, fetching: loadingCreateOwner, execute: fetchCreateOwnership } = useCreateOwnership();
  const { data: createResponse, fetching, execute: createResponseTask } = useCreateResponseTask();
  const { data: responseRemove, fetching: loadingRemove, execute: removeTaskResponse } = useRemoveTaskResponse();
  const { data: responseDeleteOwner, execute: deleteOwner } = useDeleteOwnership();
  const { reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: ownership.taskResponse.task._id } },
    pause: true
  });

  const [modalComplete, setModalComplete] = useState<boolean>(false);
  const [modalFinishTask, setModalFinishTask] = useState<boolean>(false);
  const [saveEdit, setSaveEdit] = useState<string>();
  const [percentageOwner, setPercentageOwner] = useState<number>();
  const [pendingUpdate, setPendingUpdate] = useState<any>();
  const [showErrorBox, setShowErrorBox] = useState(false);

  useEffect(() => {
    if (responseCreateOwner) {
      const ownerId = responseCreateOwner._id;
      const payload = pendingUpdate;
      if (payload) {
        const { files, ...pendingCreateResponse } = payload;
        pendingCreateResponse.metadata.ownerId = ownerId;
        createResponseTask(pendingCreateResponse);
      }
    }
  }, [responseCreateOwner])

  useEffect(() => {
    if (responseRemove)
      getTaskResponses({
        requestPolicy: 'network-only',
        variables: { taskId: ownership.taskResponse.task._id }
      });
  }, [responseRemove]);

  useEffect(() => {
    if (ownership?.ownership) setPercentageOwner(ownership.ownership);
  }, [ownership]);

  useEffect(() => {
    if (!isUpdatePercentage) form.resetFields()
  }, [ownership]);

  useEffect(() => {
    if (createResponse) {
      setSaveEdit('saved');
      //Cerrar card cuadno esta completo
      if (createResponse.state === TaskState.Draft) changeEdit('close');
    }
  }, [createResponse]);

  const changeEdit = (newState?: 'close') => {
    if (handlerSwitchExpandCard)
      handlerSwitchExpandCard(ownership._id || (ownership as any).ownerId, newState === 'close');
    setSaveEdit('');
  };

  const saveChanges = async (values: any, files: any, newState?: TaskState) => {
    isUpdatePercentage = false;

    const {
      files: ignoreFile,
      task,
      __typename,
      createdAt,
      ...taskResponse
    } = ownership.taskResponse;

    let changeState = taskResponse.state;
    
    if (
      changeState === TaskState.Incomplete &&
      isValidForm(values, files) &&
      entity?._id
    ) {
      const { _id, ownerId, referenceId, loans, ...metadata } = taskResponse.metadata;
      
      await fetchCreateOwnership({
        ...metadata,
        ...values,
        filesExists: ownership?.files,
        files: files,
        entity: entity._id,
        loan: loan._id,
        state: TaskState.Draft,
        stakes: {
          [entity._id]: ownership.ownership
        },
        _id: ownerId || _id
      });
      //createGuarantorState usarlo en el primer draft
      changeState = TaskState.Draft;
    } else if (changeState !== TaskState.Incomplete) {
      changeState = TaskState.Draft;
    }

    if (newState) changeState = newState;

    const payload = {
      ...taskResponse,
      task: task._id,
      state: changeState,
      metadata: { ...taskResponse.metadata, ...values },
      files
    };

    setPendingUpdate(payload);
    await createResponseTask(payload);
  };

  const verifyProfile = async () => {
    isUpdatePercentage = false;

    const {
      files: ignoreFile,
      task,
      __typename,
      createdAt,
      ...taskResponse
    } = ownership.taskResponse;

    await createResponseTask({
      ...taskResponse,
      task: task._id,
      state: TaskState.Draft,
      metadata: taskResponse.metadata,
    });
  }

  const changeOwnership = async (_ownership: any) => {
    isUpdatePercentage = true;

    const {
      files: ignoreFile,
      task,
      __typename,
      createdAt,
      ...taskResponse
    } = ownership.taskResponse;

    let isCompleteProfile = false;
    
    if (_ownership && entity &&
      taskResponse.metadata.address &&
      taskResponse.metadata.dateBirth &&
      taskResponse.metadata.email &&
      taskResponse.metadata.phone &&
      taskResponse.metadata.ssn &&
      ignoreFile?.length > 0
    ) {
      isCompleteProfile = true;
      const { ownerId, referenceId, ...metadata } = taskResponse.metadata;
      
      await fetchCreateOwnership({
        ...metadata,
        stakes: { ...ownership.stakes, [entity._id]: _ownership },
        entity: entity._id,
        loan: loan._id,
        _id: ownerId
      });
    }

    await createResponseTask({
      ...taskResponse,
      task: task._id,
      state: _ownership < 20 ?
        TaskState.Draft : isCompleteProfile && !taskResponse?.metadata?._id ?
          TaskState.Draft : TaskState.Incomplete,
      metadata: { ...taskResponse.metadata, ownership: _ownership }
    });

    setNewPercentageLiveEdit(null);
  };

  const deleteOwnership = async () => {
    removeTaskResponse({ taskResponseId: ownership.key });

    const ownerId = ownership?._id || (ownership as any)?.ownerId;

    if (isOwnerModelEdit && ownerId) 
      deleteOwner({ ownerId, entityId: entity._id, loanId: loan._id });

    if (onDeleteOwner)
      onDeleteOwner(ownerId);
  }

  const debouncedChange = useCallback((value: number) => {
    if (value > 0) {
      changeOwnership(value);
    }
    setNewPercentageLiveEdit({
      ownerId: (ownership._id || (ownership as any).ownerId),
      value
    });
  }, [changeOwnership, ownership]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChangeOwnership = (value: number | null) => {
    const numericValue = value || 0;
    setPercentageOwner(numericValue);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      debouncedChange(numericValue);
    }, 500);
  };

  const isValidForm = (values: any, files: any) => {
    return (values.address || ownership.address) &&
      (values.dateBirth || ownership.dateBirth) &&
      (values.email || ownership.email) &&
      (values.phone || ownership.phone) &&
      (values.ssn || ownership.ssn) &&
      (files?.length > 0 || ownership?.files?.length > 0) &&
      (values.ownership || ownership.ownership);
  }

  const tagOwnerState = (!ownership.ownership || ownership.ownership < 20) ? 'no-guarantor' :
    ownership.state === TaskState.RequestError ? 'error' :
      (!ownership._id || !isValidForm(ownership, ownership.files)) && ownership.state === TaskState.Incomplete && !stepVerify ? 'next-step' :
        !ownership._id && ownership.state === TaskState.Incomplete ? 'incomplete' :
          ownership._id && ownership.state === TaskState.Incomplete && isValidForm(ownership, ownership.files) && stepVerify ? 'verify' :
            ownership._id && ownership.state === TaskState.Incomplete && !isValidForm(ownership, ownership.files) ? 'incomplete' :
              'complete';

  const disabledRemove = loadingRemove ||
    (index === 0 && ownerships?.filter(item => item.state !== TaskState.Incomplete)?.length === 1) ||
    disabledRemovedAction;
    
  useEffect(() => {
    if (!disabledRemove || !(ownerships?.length === 1)) setShowErrorBox(false);
  }, [disabledRemove, ownerships])

  return (
    <div style={{ position: 'relative' }} id={ownership.key} className={`card-ownership ${isExpand && 'card-ownership-expand'}`}>
      {isActionRemoved && (
        <>
          <CloseCircleFilled
            className={`icon-delete ${(disabledRemove) && 'icon-delete-disabled'}`}
            onClick={!disabledRemove ? deleteOwnership : () => { }}
            disabled={disabledRemove}
          />
          {disabledRemove && (
            <div
              onClick={() => setShowErrorBox(true)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                cursor: 'not-allowed',
                backgroundColor: 'transparent'
              }}
            />
          )}
          {showErrorBox && disabledRemove && (
            <ErrorBox text='You must have at least one guarantor on this loan' img={alertIcon} className="mb-10" />
          )}
        </>
      )}
      <div
        className={`content-card ${showTagState ? 'content-card-' + ownership.state : ''
          } ${!isActionRemoved ? 'content-card-hover-removed' : ''}`}
        style={{
          height: stepVerify && !isExpand ? 74 : 'auto',
          padding: stepVerify && !isExpand ? '5px 18px 5px 16px' : '10px 18px 5px 16px'
        }}
      >
        <div className={`card-ownership`}>
          <div className="section-1">
            {showTagState && stepVerify && isExpand &&
              <StateTag
                state={ownership.state as TaskState}
                isLabelLocked
                isOtherState={ownership.state === TaskState.Draft}
                colorState={ownership.state === TaskState.Draft ? TaskState.Draft : undefined}
                isDraftState={ownership.state === TaskState.Draft}
              />
            }
            {(!stepVerify || (stepVerify && isExpand)) &&
              <div className="names" style={{ marginTop: showTagState ? 7 : 0 }}>
                <div className="name">
                  <label className="body-regular text-blue">First name</label>
                  <span>{ownership.name}</span>
                </div>
                {ownership.lastname && (
                  <div className="name">
                    <label className="body-regular text-blue">Last name</label>
                    <span>{ownership.lastname}</span>
                  </div>
                )}
              </div>
            }
            {stepVerify && !isExpand &&
              <div className="resume-names" style={{ marginTop: showTagState ? 7 : 0 }}>
                <StateTag
                  state={ownership.state}
                  isOtherState={ownership.state === TaskState.Draft}
                  isLabelLocked
                  colorState={ownership.state === TaskState.Draft ? TaskState.Draft : ownership.state as TaskState}
                  isDraftState={ownership.state === TaskState.Draft}
                />
                <div className="name">
                  <span>{ownership.name + ' ' + ownership.lastname}</span>
                </div>
              </div>
            }
            {!hiddenSectionViewProfile && (
              <TagOwnerState
                className="tag-owner-state"
                onChange={changeEdit}
                isExpand={isExpand}
                state={tagOwnerState}
                disabled={ownership.state === TaskState.Approved}
              />
            )}
          </div>
          <div className="section-2">
            {stepVerify && !isExpand &&
              <div className='ownership-section'>
                {percentageOwner && percentageOwner >= 20 &&
                  <span className="body-regular body-regular-w5 guarantor">
                    <img src={guarantorYellow} className="lienzo" alt="lienzoSvg" />{' '}
                    Guarantor
                  </span>
                }
                <InputBorderNone
                  type="number"
                  placeholder="Ownership"
                  suffix={percentageOwner ? '%' : ' '}
                  className={`input-disabled-color ${!percentageOwner && 'suffix-null'} mt-0`}
                  disabled={
                    ownership.state !== TaskState.Incomplete && ownership.state !== TaskState.RequestError && ownership.state !== TaskState.Draft
                  }
                  color="warning"
                  width={percentageOwner ? '50px' : '80px'}
                  max={100}
                  min={1}
                  value={percentageOwner}
                  onChange={(value: number) => {
                    setPercentageOwner(value);
                    setNewPercentageLiveEdit({ ownerId: (ownership._id || (ownership as any).ownerId), value });
                  }}
                  onBlur={(e: any) => {
                    if (e.target.value > 0) changeOwnership(parseInt(e.target.value));
                  }}
                />
              </div>
            }

            {(!stepVerify || (stepVerify && isExpand)) && <>
              {percentageOwner && (
                <span
                  className="body-regular text-blue"
                  style={{ fontSize: 12, marginTop: 5 }}
                >
                  Ownership
                </span>
              )}

              <InputBorderNone
                type="number"
                placeholder="Ownership"
                suffix={percentageOwner ? '%' : ' '}
                className={`input-disabled-color ${!percentageOwner && 'suffix-null mt-20'}`}
                color="warning"
                height={20}
                width={'100px'}
                max={100}
                min={1}
                value={percentageOwner}
                disabled={enabledDataBasic !== undefined ? enabledDataBasic : (disabledForm || disabledPercentage)}
                onChange={handleChangeOwnership}
              />
              {!hiddenGuarantorTag && percentageOwner && percentageOwner >= 20 && (!stepVerify || (stepVerify && isExpand)) && (
                <span className="body-regular body-regular-w5 guarantor">
                  <img src={guarantorYellow} className="lienzo" alt="lienzoSvg" />{' '}
                  Guarantor
                </span>
              )}
            </>
            }
          </div>
        </div>
        {(hiddenSectionViewProfile || isExpand) && (
          <>
            {!hiddenSectionViewProfile && (
              <Divider style={{ margin: '2px 0 5px' }} />
            )}
            <FormEdit
              ownership={ownership}
              ownerships={ownerships}
              hiddenTextEdit={hiddenTextEdit}
              disabledForm={disabledForm}
              hiddenSubmitChanges={hiddenSubmitChanges}
              hiddenColorsState={hiddenColorsState}
              saveChanges={saveChanges}
              isModalSubmit={isModalSubmit}
              saveEdit={fetching ? 'loading' : saveEdit}
              fetching={fetching || loadingCreateOwner}
              otherButton={otherButton}
              stepVerify={stepVerify}
              verifyProfile={verifyProfile}
              isExpand={isExpand}
              createResponse={createResponse}
            />
          </>
        )}

        {isExpand &&
          <IssuesTaskResponse
            taskId={ownership.taskResponse.task._id}
            taskResponseId={ownership.taskResponse._id}
            componentDataError={(textDataError: any) => (
              <div className='text-data'>
                <div className='row'>
                  <span>SSN</span>
                  <b>{textDataError.ssn}</b>
                </div>
                <div className='row'>
                  <span>Date of birth</span>
                  <b>{textDataError.dateBirth}</b>
                </div>
                <div className='row'>
                  <span>Phone number</span>
                  <b>{textDataError.phone}</b>
                </div>
                <div className='row'>
                  <span>Email address</span>
                  <b>{textDataError.email}</b>
                </div>
                <div className='row'>
                  <span>Home address</span>
                  <b>{textDataError.address}</b>
                </div>
              </div>
            )}
          />
        }

        <ModalCompleteProfile
          visible={modalComplete}
          setVisible={setModalComplete}
          closeTask={closeTask}
        />
        <ModalFinishTask
          visible={modalFinishTask}
          setVisible={setModalFinishTask}
          closeTask={closeTask}
        />
      </div>
    </div>
  );
};
export default CardOwnership;
